import { FC, ReactElement, useEffect, useRef, useState } from "react";
import { useWebTranslation } from "translations";
import Image from "next/image";
import { Typography } from "ui-kit/Typography";
import { PaymentMethodState } from "../usePaymentMethodState";
import TextInput from "ui-kit/TextInput/TextInput";
import {
  validateIBAN,
  friendlyFormatIBAN,
  getCountrySpecifications,
  electronicFormatIBAN,
} from "ibantools";
import { getCountryCode } from "utils/site";
import classNames from "classnames";
import Gutter from "ui-kit/Gutter";
import styles from "./TrustlyManualComponent.module.css";
import TrustlySecure from "./TrustlySecure";
import { SelectablePaymentMethodEnum } from "config/site";
import { FlexCol } from "ui-kit/Layout/Flex";
import { Icon } from "ui-kit";
import Colors from "ui-kit/colors";

interface TrustlyComponentProps {
  paymentMethod: SelectablePaymentMethodEnum;
  paymentMethodState: PaymentMethodState;
  setPaymentMethodState: ({ isValid, data }: PaymentMethodState) => void;
}

const countryCode = getCountryCode();
const countrySpecifications = getCountrySpecifications();
const defaultCountrySpecification = countrySpecifications[countryCode];
const ibanMaxLengthIncludingSpaces = 37;
const placeHolder =
  friendlyFormatIBAN(
    countryCode.padEnd(defaultCountrySpecification.chars ?? 2, "0")
  ) ?? "";

const shouldSetError = (iban: string) => {
  if (!iban) {
    return false;
  }

  if (iban.length === 1) {
    return !iban.match(/[A-Z]/);
  }

  const countrySpecification = countrySpecifications[iban.slice(0, 2)];

  if (!countrySpecification) {
    return true;
  }

  const validLength = countrySpecification.chars ?? 0;

  if (iban.length === validLength) {
    return !validateIBAN(iban).valid;
  }

  if (iban.length > validLength) {
    return true;
  }

  return !iban.match(/^[A-Z]{2}\d*$/);
};

const TrustlyComponent: FC<TrustlyComponentProps> = ({
  paymentMethod,
  paymentMethodState,
  setPaymentMethodState,
}): ReactElement => {
  const { t } = useWebTranslation();
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  useEffect(() => {
    if (paymentMethod === SelectablePaymentMethodEnum.TrustlyMddManual) {
      setPaymentMethodState({ isValid: false, data: {} });
    } else {
      setError(false);
    }
  }, [paymentMethod, setPaymentMethodState]);

  const iban = paymentMethodState.data?.iban ?? "";
  const currentValue = friendlyFormatIBAN(iban) ?? "";
  const icon =
    iban && paymentMethodState.isValid ? (
      <Icon
        name="trustly-checkmark"
        size="M"
        color={Colors.green300}
        className={styles.icons}
      />
    ) : error ? (
      <Icon
        name="trustly-error"
        size="M"
        color={Colors.orange300}
        className={styles.icons}
      />
    ) : null;

  return (
    <FlexCol>
      <Typography>{t("payment_methods.iban")}</Typography>
      <Gutter size={0.5} />
      <div className={styles.container}>
        <TextInput
          name="iban"
          placeHolder={placeHolder}
          value={currentValue}
          onChangeText={(value) => {
            const formattedValue = electronicFormatIBAN(value) ?? "";
            setError(shouldSetError(formattedValue));
            setPaymentMethodState({
              isValid: validateIBAN(formattedValue).valid,
              data: { iban: electronicFormatIBAN(formattedValue) },
            });
          }}
          className={classNames(styles.input, error && styles.inputError)}
          maxLength={ibanMaxLengthIncludingSpaces}
          nativeRef={inputRef}
        />
        {icon && <div className={styles.icon}>{icon}</div>}
        <Gutter size={3} />
        <Typography variant="body4" color="grey500">
          {t("payment_methods.lassie_sepa_mandate")}
        </Typography>
        <Gutter size={2} />
        <TrustlySecure />
      </div>
    </FlexCol>
  );
};

export default TrustlyComponent;
